import {Component, Input, OnInit, ViewChild} from '@angular/core';
import _ from "lodash";
import {DICTIONARY} from "../../dictionary";
import {RouteService} from "../../services/routeService";
import {GeneralService} from "../../services/generalService";
import {NotificationService} from "../../services/notificationService";
import {LookAndFeelService} from "../../services/lookAndFeelService";
import {CollaborationProtectionService} from "../../services/collaborationProtectionService";
import {ChartComponent} from "ng-apexcharts";

@Component({
	selector: 'collaboration-rule-usage-c',
	templateUrl: './collaboration-rule-usage.component.html',
})
export class CollaborationRuleUsageComponent implements OnInit {

	@Input() data: any;

	constructor(private rs:RouteService,
				public gs:GeneralService,
				public lfs:LookAndFeelService,
				private ns:NotificationService,
				private cps:CollaborationProtectionService) { }

	@ViewChild('assetsActivityChart', {static: false}) assetsActivityChart: ChartComponent;
	@ViewChild('sensitivityTypesChart', {static: false}) sensitivityTypesChart: ChartComponent;
	@ViewChild('threatTypesChart', {static: false}) threatTypesChart: ChartComponent;

	ngOnInit(): void {
		this.getRuleUsage(this.data.ruleObj, this.data.period);
	}

	dic = DICTIONARY;
	usagePeriod = DICTIONARY.CONSTANTS.trendsPeriod.lastWeek;
	viewTypes = {chart: 'Chart', list: 'List'};
	viewType = this.viewTypes.list;
	getRuleUsageInProcess;
	assets;
	assetsActivityChartOptions;
	sensitivityTypesChartOptions;
	threatTypesChartOptions;
	involvedUsersChartOptions;
	involvedUsersList;
	currentAssetInfoObj;

	trendsPeriod = [
		this.dic.CONSTANTS.trendsPeriod.lastDay,
		this.dic.CONSTANTS.trendsPeriod.last3Days,
		this.dic.CONSTANTS.trendsPeriod.lastWeek,
		this.dic.CONSTANTS.trendsPeriod.lastMonth,
	];

	closeModal = () => {
		this.data.show = false;
	};

	getRuleUsage = (ruleObj, period = null) => {
		this.getRuleUsageInProcess = true;
		const ruleAction:any = {
			action: this.dic.CONSTANTS.collaborationProtectionRule.actions.usage,
			period: this.usagePeriod.value,
			size: this.dic.CONSTANTS.quarantinedEmailsFirstBatchSize,
		};

		this.rs.updateCollaborationProtectionRule(ruleObj._id, ruleAction).then(response => {
			this.assets = response?.assets || [];
			this.assets.forEach((assetObj) => {
				this.cps.parseResourceName(assetObj.resource_id);
			});

			this.prepareGraphInfo(false);
			this.getNextAssetsBatch(ruleObj, response, () => {
				this.getRuleUsageInProcess = false;
			});
		}, (err) => {
			this.getRuleUsageInProcess = false;
		});
	};

	getNextAssetsBatch = (ruleObj, assetsInfo, cb) => {
		if (!assetsInfo.lastCreated) {
			return cb();
		}

		const ruleData = {
			action: this.dic.CONSTANTS.collaborationProtectionRule.actions.usage,
			period: this.usagePeriod.value,
			size: this.dic.CONSTANTS.quarantinedEmailsBatchSize,
			lastCreated: assetsInfo.lastCreated
		};

		this.rs.updateCollaborationProtectionRule(ruleObj._id.toString(), ruleData).then(response => {
			if (response.assets?.length) {
				response.assets.forEach((assetObj) => {
					this.cps.parseResourceName(assetObj.resource_id);
				});
				this.assets = this.assets.concat(response.assets);
				this.prepareGraphInfo(true);
			}

			this.getNextAssetsBatch(ruleObj, response, cb);
		});
	}

	prepareGraphInfo = (isUpdate) => {
		this.prepareAssetsActivityChart(isUpdate);
		this.prepareInvolvedUsersChart(isUpdate);
		this.prepareSensitivityTypesChart(isUpdate);
		this.prepareThreatTypesChart(isUpdate);
	}

	prepareAssetsActivityChart = (isUpdate) => {
		const activityStats = {
			labels: [],
			data: [[]],
			series: [this.data.ruleObj.name]
		};

		// @ts-ignore
		const sortedAssets = [...this.assets].sort((a, b) => new Date(a.created) - new Date(b.created));

		let sourcePerPeriod: any = {};

		for (let i = 0; i < sortedAssets.length; i++) {
			const assetObj = sortedAssets[i];

			if (!sourcePerPeriod.dateStart) {
				sourcePerPeriod = this.gs.calcDatesForPeriod(assetObj, this.usagePeriod.value);
				sourcePerPeriod.total = 0;
			}

			if (this.gs.isSamePeriod(this.usagePeriod.value, assetObj.created, sourcePerPeriod.dateEnd)) {
				sourcePerPeriod.total += 1;
			}
			else {
				this.addActivityGraphData(activityStats, sourcePerPeriod);
				sourcePerPeriod = this.gs.calcDatesForPeriod(assetObj, this.usagePeriod.value);
				sourcePerPeriod.total = 0;
				sourcePerPeriod.total += 1;
			}
		}
		this.addActivityGraphData(activityStats, sourcePerPeriod);

		const series = [];
		activityStats.series.forEach((categoryName, index) => {
			series.push({
				name: 'Assets',
				data: activityStats.data[index]
			});
		});

		if (isUpdate && this.assetsActivityChart && this.assetsActivityChartOptions?.xaxis?.categories?.length &&
			this.gs.areArraysEqual(activityStats.labels, this.assetsActivityChartOptions.xaxis.categories)) {
			this.assetsActivityChart.updateSeries(series, true);
			return;
		}

		this.assetsActivityChartOptions = {
			series: series,
			chart: {
				type: activityStats.labels?.length === 1 ? 'bar' : 'area',
				height: '100%',
				toolbar: {
					show: false
				},
				animations: {
					animateGradually: {
						enabled: false,
					},
				}
			},
			plotOptions: {
				bar: {
					borderRadius: 5,
					horizontal: false,
					columnWidth: this.gs.getOptimalBarWidth(activityStats.labels.length) - 20 + '%',
				},
			},
			colors: ['#8177a3'],
			dataLabels: {
				enabled: false
			},
			stroke: {
				show: true,
				width: 4,
				colors: ['transparent']
			},
			xaxis: {
				categories: activityStats.labels,
			},
			yaxis: {
				title: {
					text: 'Assets'
				},
				min: 0,
				labels: {
					formatter: (val) => {
						return val.toFixed(0);
					}
				}
			},
			fill: {
				opacity: 1
			},
			tooltip: {
				y: {
					formatter: (val) => {
						return val.toFixed(0);
					}
				}
			}
		};
	}

	addActivityGraphData(activityStats, sourcePerPeriod) {
		if (sourcePerPeriod.dateStart) {
			const dateLabel = this.gs.getPeriodLabel(this.usagePeriod.value, sourcePerPeriod);
			activityStats.labels.push(dateLabel);
			activityStats.data[0].push(sourcePerPeriod.total);
		}
	}

	prepareSensitivityTypesChart = (isUpdate) => {
		const classificationTypes = {};

		this.assets?.forEach((assetObj) => {
			if (!assetObj.sensitivity_info) {
				return;
			}
			assetObj.sensitivity_info.sensitive_types?.forEach((sensitiveTypeObj) => {
				if (sensitiveTypeObj.score) {
					if (!classificationTypes[sensitiveTypeObj.sensitive_type]) {
						classificationTypes[sensitiveTypeObj.sensitive_type] = 0;
					}
					classificationTypes[sensitiveTypeObj.sensitive_type]++;
				}
			});
		});

		// @ts-ignore
		const sortedTypes = Object.fromEntries(Object.entries(classificationTypes).sort((a, b) => b[1] - a[1]));
		const sortedTypesArray = Object.entries(sortedTypes).map(([type, count]) => ({type, count}));

		const series = [];
		const labels = [];
		for (let i = 0; i < sortedTypesArray.length && i < 7; i++) {
			series.push(sortedTypesArray[i].count);
			labels.push(sortedTypesArray[i].type);
		}

		if (isUpdate && this.sensitivityTypesChart && this.sensitivityTypesChartOptions?.labels?.length && this.gs.areArraysEqual(this.sensitivityTypesChartOptions.labels, labels)) {
			this.sensitivityTypesChart.updateSeries(series, true);
			return;
		}

		this.sensitivityTypesChartOptions = {
			series,
			chart: {
				type: 'pie',
				width: '100%',
			},
			colors: this.gs.chartColors,
			labels: labels,
			plotOptions: {
				pie: {
					donut: {
						size: '52%',
					}
				}
			},
			dataLabels: {
				enabled: true,
				formatter: (val, opt) => {
					return opt.w.globals.series[opt.seriesIndex];
				}
			},
		};
	}

	prepareThreatTypesChart = (isUpdate) => {
		const maliciousTypes = {};

		this.assets?.forEach((assetObj) => {
			if (!assetObj.malicious_info) {
				return;
			}
			assetObj.malicious_info.sub_status?.forEach((subStatusObj) => {
				if (!maliciousTypes[subStatusObj.sub_status]) {
					maliciousTypes[subStatusObj.sub_status] = 0;
				}
				maliciousTypes[subStatusObj.sub_status]++;
			});
		});

		// @ts-ignore
		const sortedTypes = Object.fromEntries(Object.entries(maliciousTypes).sort((a, b) => b[1] - a[1]));
		const sortedTypesArray = Object.entries(sortedTypes).map(([type, count]) => ({type, count}));

		const series = [];
		const labels = [];
		for (let i = 0; i < sortedTypesArray.length && i < 10; i++) {
			series.push(sortedTypesArray[i].count);
			labels.push(sortedTypesArray[i].type);
		}

		if (isUpdate && this.threatTypesChart && this.threatTypesChartOptions?.labels?.length && this.gs.areArraysEqual(this.threatTypesChartOptions.labels, labels)) {
			this.threatTypesChart.updateSeries(series, true);
			return;
		}

		this.threatTypesChartOptions = {
			series,
			chart: {
				type: 'donut',
				width: '100%',
			},
			plotOptions: {
				pie: {
					donut: {
						size: '52%',
					}
				}
			},
			dataLabels: {
				enabled: true,
				formatter: (val, opt) => {
					return opt.w.globals.series[opt.seriesIndex];
				}
			},
			colors: ['#D92D20', '#F79009', '#7A5AF8', '#2E90FA', '#0e9f8e', '#486db5', '#e55353', '#94a3b8'],
			labels,
		};
	}

	prepareInvolvedUsersChart = (isUpdate) => {
		const involvedUsers = {};

		this.assets?.forEach((assetObj) => {
			if (!assetObj.user) {
				return;
			}

			if (!involvedUsers[assetObj.user]) {
				involvedUsers[assetObj.user] = 0;
			}
			involvedUsers[assetObj.user]++;
		});

		this.involvedUsersList = Object.entries(involvedUsers)
			.map(([email, hits]) => ({email, hits}))
			.sort((a, b) => (b.hits as number) - (a.hits as number))
			.slice(0, 10);
	}

	changePeriod = (period) => {
		if (this.usagePeriod === period) {
			return;
		}
		this.usagePeriod = period;
		this.getRuleUsage(this.data.ruleObj);
	};

	changeViewType = (type) => {
		this.viewType = type;
	}

	showAssetActions = () => {
		return ['View info'];
	}

	selectAssetAction = (assetObj, action) => {
		switch (action) {
			case 'View info':
				this.currentAssetInfoObj = assetObj;
				break;
		}
	}

	searchRuleAsset = (searchTerm) => {
		this.assets.forEach(record => {
			if (searchTerm) {
				const isFound = searchAssetTextExecute(record, searchTerm);
				if (!isFound) {
					record.hide = true;
					return;
				}
			}

			record.hide = false;
		});
	}

	exportRuleAssetsToCsv = (sortBy) => {
		if (!this.assets || !this.assets.length) {
			this.ns.showWarnMessage(this.dic.ERRORS.noDataToExportCsv);
			return;
		}
		let csvString = "User,Resource,Type,Created,Category\n";

		let sortedTable = _.filter(this.assets, log => {
			return !log.hide;
		});
		if (sortBy) {
			sortedTable = this.gs.sortTable(sortedTable, sortBy);
		}

		sortedTable.forEach((asset) => {
			csvString += `"${asset.user || ''}","${asset.resource_id?.name || ''}","${asset.asset_type || ''}",${asset.created || ''},"${asset.category || ''}"\n`;
		});

		this.gs.exportCsv(csvString, `collaboration_rule_assets.csv`);
	};
}

function searchAssetTextExecute(asset, searchTerm) {
	searchTerm = searchTerm.toLowerCase();
	return (asset.file_name?.toLowerCase().indexOf(searchTerm) > -1 ||
		asset.user?.toLowerCase().indexOf(searchTerm) > -1 ||
		asset.resource_id?.name?.toLowerCase().indexOf(searchTerm) > -1 ||
		asset.category?.toLowerCase().indexOf(searchTerm) > -1);
}
